import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { cva, VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { buttonVariants } from './button';
import { cn } from './cn';
import { useRestoreFocusToInvoker } from './restoreFocus';
import { usePortalContainer } from './ui-provider';

/**
 * A dialog that demands an answer: no outside-click dismissal, focus starts on the confirming
 * action so Enter confirms and Escape cancels. Use it for decisions (delete, discard, unlock);
 * use `Dialog` for forms and content. `useConfirm` from
 * `./confirm` wraps it for the one-line `await confirm(...)` case.
 */
export const AlertDialog = AlertDialogPrimitive.Root;
export const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

// Decisions sit above every other layer, the legacy Forge modals (z-index 1300) included.
const alertDialogContentVariants = cva(
  'fixed left-1/2 top-1/2 z-[1410] grid w-[calc(100%_-_2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-lg border border-border bg-background p-6 text-foreground shadow-[var(--shadow-elevated)] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
  {
    variants: { size: { default: 'max-w-lg', sm: 'max-w-sm' } },
    defaultVariants: { size: 'default' },
  }
);

export const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content> &
    VariantProps<typeof alertDialogContentVariants>
>(({ className, size, children, onOpenAutoFocus, onCloseAutoFocus, ...props }, ref) => {
  const { OpenerCapture, restoreFocus } = useRestoreFocusToInvoker();
  const container = usePortalContainer();
  if (!container) return null;
  return (
    <AlertDialogPrimitive.Portal container={container}>
      <AlertDialogPrimitive.Overlay
        className="fixed inset-0 z-[1400] bg-[var(--scrim)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        // A scrim click must not move focus out of the decision.
        onMouseDown={(event) => event.preventDefault()}
      />
      <AlertDialogPrimitive.Content
        ref={ref}
        className={cn(alertDialogContentVariants({ size }), className)}
        // Radix starts on Cancel; the product rule is that Enter performs the action. A caller
        // that focuses something else (the prompt's entry) preventDefaults first.
        onOpenAutoFocus={(event) => {
          onOpenAutoFocus?.(event);
          const content = event.currentTarget;
          if (event.defaultPrevented || !(content instanceof HTMLElement)) return;
          const action =
            content.querySelector<HTMLElement>('[data-alert-dialog-action]:not(:disabled)') ??
            content.querySelector<HTMLElement>('button[type="submit"]:not(:disabled)');
          if (!action) return;
          event.preventDefault();
          action.focus({ preventScroll: true });
        }}
        // Opened from actions and shortcuts, not an AlertDialogTrigger; Radix would otherwise
        // drop focus on <body> when the decision closes.
        onCloseAutoFocus={(event) => {
          onCloseAutoFocus?.(event);
          if (!event.defaultPrevented) restoreFocus(event);
        }}
        {...props}
      >
        <OpenerCapture />
        {children}
      </AlertDialogPrimitive.Content>
    </AlertDialogPrimitive.Portal>
  );
});
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

/** Title and description; drop an `AlertDialogMedia` in first to get an icon beside them. */
export const AlertDialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'grid gap-1.5 text-left has-[>[data-slot=alert-dialog-media]]:grid-cols-[auto_1fr] has-[>[data-slot=alert-dialog-media]]:gap-x-3',
      className
    )}
    {...props}
  />
);

export const AlertDialogMedia = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-slot="alert-dialog-media"
    className={cn(
      'row-span-2 flex size-9 items-center justify-center rounded-full bg-muted text-foreground [&_svg]:size-4',
      className
    )}
    {...props}
  />
);

export const AlertDialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
    {...props}
  />
);

export const AlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={cn('text-base font-semibold leading-tight tracking-tight', className)}
    {...props}
  />
));
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

export const AlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
AlertDialogDescription.displayName = AlertDialogPrimitive.Description.displayName;

/**
 * The confirming button, focused when the dialog opens so Enter confirms; pass
 * `variant="destructive"` when the action destroys something.
 */
export const AlertDialogAction = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action> &
    VariantProps<typeof buttonVariants>
>(({ className, variant, size, ...props }, ref) => (
  <AlertDialogPrimitive.Action
    ref={ref}
    data-alert-dialog-action=""
    className={cn(buttonVariants({ variant, size }), className)}
    {...props}
  />
));
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

export const AlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Cancel
    ref={ref}
    className={cn(buttonVariants({ variant: 'outline' }), className)}
    {...props}
  />
));
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;
